import React from 'react';
import Image from 'next/image';

interface TeamShowcaseCardProps {
  imageUrl: string;
  imageAlt: string;
  title: string;
  description: string;
  /** Resolved width string set by the parent — controls slide sizing inside the Swiper. */
  cardWidth: string;
  /** Eager-load above-the-fold instances; lazy-load on the landing page where the swiper is below the fold. */
  isLandingPage?: boolean;
}

/**
 * Single card inside the TeamSection swiper. Pure presentational — extracted
 * from TeamSection so the section file stays focused on Swiper orchestration,
 * not per-card markup.
 *
 * The `team-member-card` className on the image is intentional: TeamSection
 * queries it with `document.querySelectorAll('.team-member-card')` to wire the
 * GSAP parallax animation. Do not rename without updating that selector.
 */
const TeamShowcaseCard: React.FC<TeamShowcaseCardProps> = ({
  imageUrl,
  imageAlt,
  title,
  description,
  cardWidth,
  isLandingPage = false,
}) => {
  return (
    <div
      className="
        relative
        flex flex-col gap-[1.5rem]
      "
    >
      <div className="border-radius-main border-secondary border-stroke-soft-200 overflow-hidden">
        <Image
          src={imageUrl}
          alt={imageAlt}
          width={600}
          height={800}
          sizes="(min-width: 992px) 33vw, (min-width: 768px) 50vw, 80vw"
          loading={isLandingPage ? 'lazy' : 'eager'}
          className="team-member-card"
          style={{
            width: cardWidth,
            height: 'auto',
            scale: 1.1,
            aspectRatio: '3/4',
            objectFit: 'cover',
          }}
        />
      </div>
      <div
        style={{ direction: 'ltr' }}
        className="relative p-0
            top-0
            left-[0.5rem] lg:left-[0.25rem]
            flex flex-col gap-[1rem] lg:gap-[1.25rem]
          "
      >
        <p className="text-text-primary text-28-to-20--neue-june">
          {title}
        </p>
        <p className="text-text-quaternary text-20--alexandria">
          {description}
        </p>
      </div>
    </div>
  );
};

export default TeamShowcaseCard;
